import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MobileLayout from "@/components/MobileLayout";
import api, { SESSION_ID } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Package, Clock, Lock, Unlock, FileText, EyeOff, RotateCcw } from "lucide-react";

const statusColors = {
  "Recibido": "bg-blue-100 text-blue-800 border-blue-200",
  "Pendiente de revisar": "bg-yellow-100 text-yellow-800 border-yellow-200",
  "Falta información": "bg-orange-100 text-orange-800 border-orange-200",
  "Listo para imprimir": "bg-purple-100 text-purple-800 border-purple-200",
  "En preparación": "bg-indigo-100 text-indigo-800 border-indigo-200",
  "Terminado": "bg-green-100 text-green-800 border-green-200",
  "Entregado": "bg-slate-200 text-slate-700 border-slate-300",
  "Cancelado": "bg-red-100 text-red-800 border-red-200",
  "Incidencia": "bg-red-100 text-red-800 border-red-200",
};

function isOpen(o) {
  if (!o.is_open) return false;
  if (!o.expires_at) return false;
  return new Date(o.expires_at) > new Date();
}

export default function MyCodes() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [hiddenInfo, setHiddenInfo] = useState({ has_hidden_recent: false, count: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const url = user ? "/orders/mine" : `/orders/guest?sid=${encodeURIComponent(SESSION_ID)}`;

      if (user) {
        const [ordersRes, hiddenRes] = await Promise.all([
          api.get(url),
          api.get("/orders/hidden/recent"),
        ]);
        setOrders(ordersRes.data || []);
        setHiddenInfo(hiddenRes.data || { has_hidden_recent: false, count: 0 });
      } else {
        const ordersRes = await api.get(url);
        setOrders(ordersRes.data || []);
        setHiddenInfo({ has_hidden_recent: false, count: 0 });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const hideOrder = async (orderId) => {
    if (!user) return;

    if (!window.confirm("¿Ocultar este código de tu lista? Podrás restaurar pedidos ocultos recientes durante 7 días.")) {
      return;
    }

    setBusy(true);
    try {
      const order = orders.find((item) => item.id === orderId);
      await api.post(`/orders/${orderId}/hide`);
      await loadOrders();
    } finally {
      setBusy(false);
    }
  };

  const restoreRecent = async () => {
    if (!user) return;

    setBusy(true);
    try {
      await api.post("/orders/hidden/restore-recent");
      await loadOrders();
    } finally {
      setBusy(false);
    }
  };

  return (
    <MobileLayout>
      <h1 className="mb-2 text-4xl font-black tracking-tight text-slate-900">Mis códigos</h1>
      <p className="mb-4 text-base text-slate-600">
        {user
          ? "Todos tus pedidos enviados a KAPEI."
          : "Pedidos enviados desde este dispositivo."}
      </p>


      {user && hiddenInfo.has_hidden_recent && (
        <div className="mb-4 rounded-2xl border-2 border-lime-200 bg-lime-50 p-4">
          <p className="text-sm font-bold text-slate-900">
            Tienes {hiddenInfo.count} código(s) oculto(s) recientemente.
          </p>
          <p className="mt-1 text-xs text-slate-600">
            Puedes restaurarlos durante 7 días si necesitas recuperar un código de recogida.
          </p>
          <button
            type="button"
            onClick={restoreRecent}
            disabled={busy}
            className="mt-3 inline-flex items-center rounded-full bg-[#4d7c0f] px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Restaurar códigos ocultos recientes
          </button>
        </div>
      )}

      {loading && <p className="text-slate-500">Cargando…</p>}

      {!loading && orders.length === 0 && (
        <div className="kapei-card text-center" data-testid="orders-empty">
          <Package className="mx-auto mb-3 h-10 w-10 text-slate-400" />
          <p className="font-bold text-slate-900">Aún no tienes códigos visibles</p>
          <p className="mt-1 text-sm text-slate-600">
            Empieza con un envío rápido{user ? " o restaura códigos ocultos recientes." : "."}
          </p>
          <Link to="/send?mode=quick" className="mt-4 inline-block rounded-full bg-[#4d7c0f] px-5 py-2 text-sm font-bold text-white">
            Nuevo pedido
          </Link>
        </div>
      )}

      <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid="orders-list">
        {orders.map((o) => {
          const open = isOpen(o);
          return (
            <li key={o.id} className="rounded-2xl border-2 border-slate-200 bg-white p-4">
              <Link
                to={`/codes/${encodeURIComponent(o.code)}`}
                data-testid={`open-order-${o.code}`}
                className="block"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-2xl font-black tracking-wider text-[#4d7c0f]">{o.code}</div>
                    <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                      <Clock className="h-3 w-3" />
                      {new Date(o.created_at).toLocaleString("es-ES")}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge
                      data-testid={`order-status-${o.code}`}
                      variant="outline"
                      className={`whitespace-nowrap border ${statusColors[o.status] || "bg-slate-100 text-slate-700"}`}
                    >
                      {o.status}
                    </Badge>
                    {open ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-[#4d7c0f]">
                        <Unlock className="h-3 w-3" /> Abierto
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500">
                        <Lock className="h-3 w-3" /> Cerrado
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-sm text-slate-700">
                  <FileText className="h-4 w-4 text-slate-500" />
                  {o.files?.length || 0} archivo(s) · {((o.total_size || 0) / 1024).toFixed(0)} KB
                </div>
                {o.note && <p className="mt-2 text-xs italic text-slate-500">"{o.note}"</p>}
              </Link>

              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <Link
                  to={`/codes/${encodeURIComponent(o.code)}`}
                  data-testid={`view-detail-${o.code}`}
                  className="rounded-full border-2 border-slate-300 px-3 py-1 font-bold text-slate-700"
                >
                  Ver detalle
                </Link>
                {open && (
                  <Link
                    to={`/send?mode=${o.mode || "quick"}&append=${o.id}`}
                    data-testid={`add-files-${o.code}`}
                    className="rounded-full border-2 border-[#4d7c0f] px-3 py-1 font-bold text-[#4d7c0f]"
                  >
                    Añadir más archivos
                  </Link>
                )}
                {user && (
                  <button
                    type="button"
                    onClick={() => hideOrder(o.id)}
                    disabled={busy}
                    className="inline-flex items-center rounded-full border-2 border-slate-300 px-3 py-1 font-bold text-slate-600 disabled:opacity-50"
                  >
                    <EyeOff className="mr-1 h-3 w-3" />
                    Ocultar
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </MobileLayout>
  );
}
